import {
  detectEcommercePlatform,
  getCsvEcommerceSubtype,
  ConnectionPlatformInput,
  CsvConnectionInput,
} from './connectionPlatform';

export type UnifiedExportContext = 'inventory' | 'reports';

/**
 * Connectors offered as selectable export sources in a given export context.
 *
 * Veeqo is a pooled inventory-overlay source, not an independently-reportable connection with
 * its own SKU report row, so it is selectable only for the Current Stock (inventory) export.
 * Offering it under reports would always export empty data.
 */
export function getExportSupportedConnectors(context: UnifiedExportContext): string[] {
  return context === 'inventory'
    ? ['shopify', 'amazon', 'veeqo']
    : ['shopify', 'amazon'];
}

/**
 * True if a connection should appear as a selectable source in UnifiedExportManager for the
 * given context and configured primary inventory source.
 *
 * `primarySource` is only honoured for the inventory context, mirroring UnifiedExportManager's
 * `hasSourceFilter`: a reports export is never narrowed by inventory_source_config. A configured
 * source that this function doesn't recognise excludes every connection, which surfaces bad
 * config as an empty picker rather than as a silently wrong export.
 *
 * Extracted so UnifiedExportManager and its tests share one implementation -- the test used to
 * keep its own copy of the supported-connector list, so dropping Veeqo from the component would
 * not have failed it.
 */
export function isExportSelectableConnection(
  connection: ConnectionPlatformInput & CsvConnectionInput,
  context: UnifiedExportContext,
  primarySource?: string | null,
): boolean {
  const platform = detectEcommercePlatform(connection);
  const csvSubtype = getCsvEcommerceSubtype(connection);
  const isCsvImport = platform === null && csvSubtype !== null;
  const connectionType = platform ?? csvSubtype ?? '';

  if (!connectionType || !getExportSupportedConnectors(context).includes(connectionType)) {
    return false;
  }

  const hasSourceFilter = context === 'inventory' && !!primarySource && primarySource !== 'all';
  if (!hasSourceFilter) return true;

  switch (primarySource!.toLowerCase()) {
    case 'shopify':
      return connectionType === 'shopify' && !isCsvImport;
    case 'amazon':
      return connectionType === 'amazon' && !isCsvImport;
    case 'shopify_csv':
      return connectionType === 'shopify' && isCsvImport;
    case 'amazon_csv':
      return connectionType === 'amazon' && isCsvImport;
    case 'veeqo':
      return connectionType === 'veeqo' && !isCsvImport;
    default:
      return false;
  }
}
